import { memo } from 'react';
import { flexRender } from '@tanstack/react-table';

// Utils
import cn from '../../../utils/cn';

// Domain
import { useTableColumnHoverDispatchOptional } from '../../../domain/table/useTableColumnHover';
import { getCommonPinningStyles, getEffectiveBodyColspan } from '../../../domain/table/tableUseCase';
import { getThSpanWidthPx } from '../../../domain/table/tableHeadUseCase';
import { useTableRowHoverDispatchOptional } from '../../../domain/table/useTableRowHover';

// Components
import CheckboxCell from '../../molecules/TableCells/CheckboxCell/CheckboxCell';

// Styles
import styles from './TableBody.module.css';

/**
 * @param {React.ChangeEvent<HTMLInputElement> | undefined} event
 * @returns {boolean}
 */
function getShiftKeyFromCheckboxChangeEvent(event) {
  const nativeEvent = event?.nativeEvent;
  if (!nativeEvent || !('shiftKey' in nativeEvent)) return false;
  return Boolean(/** @type {MouseEvent & Event} */ (nativeEvent).shiftKey);
}

/**
 * @param {{ cell: TableCell }} params
 * @returns {React.ReactNode}
 */
function defaultRenderCell({ cell }) {
  return flexRender(cell.column.columnDef.cell, cell.getContext());
}

/**
 * @param {object} props
 * @param {import('@tanstack/react-table').Row<TableRowFlattened>} props.row
 * @param {TableInstance} [props.table]
 * @param {(params: { cell: TableCell }) => React.ReactNode} [props.children]
 * @param {(groupId: string, id: string, options?: { shiftKey?: boolean }) => void} [props.handleSelectRow]
 * @param {boolean} props.isSelected
 * @param {string | null} props.activeColumnId
 * @param {number} props.tableWidth
 * @param {React.Ref<HTMLTableRowElement>} [props.measureRef]
 * @param {number} [props.virtualIndex]
 * @param {boolean} [props.isFindCurrent]
 * @returns {JSX.Element}
 */
function TableBodyRow({
  row,
  table,
  children = defaultRenderCell,
  handleSelectRow,
  isSelected,
  activeColumnId,
  tableWidth,
  measureRef,
  virtualIndex,
  isFindCurrent = false,
}) {
  const { setHoveredLeafIndices, leafIndicesForColumnRange } = useTableColumnHoverDispatchOptional();
  const { setHoveredRow } = useTableRowHoverDispatchOptional();
  const rowOriginal = row.original;
  const isRowDeletingOptimistic = rowOriginal.isDeletingOptimistic ?? false;
  const visibleCells = /** @type {TableCell[]} */ (row.getVisibleCells());
  const rowIsEditable = rowOriginal.editable;

  /** @type {JSX.Element[]} */
  const rowCells = [];

  for (let i = 0; i < visibleCells.length;) {
    const cell = visibleCells[i];
    const { columnDef: columnDefRaw } = cell.column;
    const columnDef = /** @type {TableColumnsReactTable} */ (columnDefRaw);
    const isColumnCheckbox = columnDef?.type === 'static-checkbox';
    const isColumnAddingOptimistic = columnDef?.isAddingColumnOptimistic ?? false;

    const cellValue = /** @type {TableCellValueObjectCustom | undefined} */ (cell.getValue());
    let colSpan = getEffectiveBodyColspan(cellValue, columnDef);
    if (!isColumnCheckbox) {
      const maxSpan = visibleCells.length - i;
      colSpan = Math.min(colSpan, maxSpan);
    }

    const isCellSelectable = !isColumnCheckbox
      && rowIsEditable
      && handleSelectRow;

    const pinStyles = getCommonPinningStyles(cell.column);
    const mergedStyle = table && colSpan > 1
      ? {
        ...pinStyles,
        width: getThSpanWidthPx(table, cell.column.id, colSpan),
        maxWidth: getThSpanWidthPx(table, cell.column.id, colSpan),
      }
      : pinStyles;

    rowCells.push(
      <td
        className={cn([
          styles['table-body-cell'],
          cell.column.getIsPinned() && styles['table-body-cell-pinned'],
          isColumnCheckbox && styles['table-body-cell-checkbox'],
          isColumnAddingOptimistic && styles['table-body-cell-adding-optimistic'],
          isCellSelectable && styles['table-body-cell-selectable'],
        ])}
        key={cell.id}
        colSpan={colSpan > 1 ? colSpan : undefined}
        style={mergedStyle}
        onMouseEnter={() => {
          setHoveredLeafIndices(leafIndicesForColumnRange(cell.column.id, colSpan));
        }}
        onClick={isCellSelectable ? (e) => handleSelectRow(row.original.$groupId, row.original.id, { shiftKey: e.shiftKey }) : undefined}
      >
        {isColumnCheckbox && rowIsEditable ? (
          <CheckboxCell
            value={isSelected}
            align="center"
            onChange={(_newValue, event) => handleSelectRow(
              row.original.$groupId,
              row.original.id,
              { shiftKey: getShiftKeyFromCheckboxChangeEvent(event) },
            )}
          />
        ) : null}
        {!isColumnCheckbox && children ? children({ cell }) : null}
      </td>,
    );

    i += colSpan;
  }

  return (
    <tr
      ref={measureRef}
      className={cn([
        styles['table-body-row'],
        isRowDeletingOptimistic && styles['table-body-row-deleting-optimistic'],
      ])}
      key={rowOriginal.id}
      data-index={virtualIndex}
      data-row-id={rowOriginal.id}
      data-find-current={isFindCurrent ? '' : undefined}
      data-active-column={activeColumnId ?? undefined}
      data-table-width={tableWidth}
      onMouseEnter={(e) => {
        setHoveredRow(rowOriginal, e.currentTarget);
      }}
    >
      {rowCells}
    </tr>
  );
}

/**
 * @param {object} prev
 * @param {object} next
 * @returns {boolean}
 */
function tableBodyRowPropsAreEqual(prev, next) {
  return prev.row.original === next.row.original
    && prev.isSelected === next.isSelected
    && prev.activeColumnId === next.activeColumnId
    && prev.tableWidth === next.tableWidth
    && prev.handleSelectRow === next.handleSelectRow
    && prev.table === next.table
    && prev.children === next.children
    && prev.measureRef === next.measureRef
    && prev.virtualIndex === next.virtualIndex
    && prev.isFindCurrent === next.isFindCurrent;
}

export default memo(TableBodyRow, tableBodyRowPropsAreEqual);
